import { Copy, Lock, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import { cx } from '@/lib/cx';
import { mensagemDeErro } from '@/lib/erros';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { CabecalhoPagina } from '@/ui/patterns/cabecalho-pagina';
import { toast } from '@/ui/primitives/aviso';
import { Badge } from '@/ui/primitives/badge';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { Campo, Input } from '@/ui/primitives/input';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type Perfil, type Permissao } from '@fiscoai/contracts';

import { useAlterarPerfis, usePerfis } from './api';
import { MatrizPermissoes } from './matriz-permissoes';

function Editor({ perfil }: { perfil: Perfil }) {
  const { atualizar, criar } = useAlterarPerfis();
  const [marcadas, setMarcadas] = useState<Permissao[]>(perfil.permissoes);
  const [copia, setCopia] = useState(false);
  const [nome, setNome] = useState('');
  const [chave, setChave] = useState('');
  const mudou =
    marcadas.length !== perfil.permissoes.length ||
    marcadas.some((p) => !perfil.permissoes.includes(p));
  const aviso = { onError: (erro: unknown) => toast.error(mensagemDeErro(erro)) };

  return (
    <section className="grid content-start gap-5 rounded-painel border border-borda bg-superficie p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-1">
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            {perfil.nome}
            {perfil.sistema && (
              <Badge>
                <Lock aria-hidden />
                Do sistema
              </Badge>
            )}
          </h2>
          <p className="text-sm text-texto-suave">{perfil.descricao ?? 'Sem descrição'}</p>
        </div>
        <div className="flex gap-2">
          <Button
            tamanho="sm"
            onClick={() => {
              setCopia(true);
            }}
          >
            <Copy aria-hidden />
            Duplicar
          </Button>
          {!perfil.sistema && mudou && (
            <Button
              tamanho="sm"
              variante="primario"
              carregando={atualizar.isPending}
              onClick={() => {
                atualizar.mutate(
                  { id: perfil.id, dados: { permissoes: marcadas } },
                  { ...aviso, onSuccess: () => toast.success('Permissões salvas') },
                );
              }}
            >
              Salvar permissões
            </Button>
          )}
        </div>
      </header>
      <MatrizPermissoes
        escopo={perfil.escopo}
        marcadas={marcadas}
        aoMudar={setMarcadas}
        somenteLeitura={perfil.sistema}
      />
      <Dialog
        aberto={copia}
        aoMudar={setCopia}
        titulo={`Novo perfil a partir de ${perfil.nome}`}
        descricao="As permissões são copiadas; ajuste depois."
        acoes={
          <Button
            variante="primario"
            carregando={criar.isPending}
            onClick={() => {
              criar.mutate(
                { chave, nome, descricao: null, escopo: perfil.escopo, permissoes: marcadas },
                {
                  ...aviso,
                  onSuccess: () => {
                    setCopia(false);
                    toast.success('Perfil criado');
                  },
                },
              );
            }}
          >
            Criar perfil
          </Button>
        }
      >
        <div className="grid gap-4">
          <Campo rotulo="Nome">
            {(a) => (
              <Input
                {...a}
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value);
                }}
              />
            )}
          </Campo>
          <Campo rotulo="Chave" ajuda="Letras minúsculas, números e _ (não muda depois).">
            {(a) => (
              <Input
                {...a}
                className="font-mono"
                value={chave}
                onChange={(e) => {
                  setChave(e.target.value);
                }}
              />
            )}
          </Campo>
        </div>
      </Dialog>
    </section>
  );
}

export function PaginaPerfis() {
  const consulta = usePerfis();
  const [selecionadoId, setSelecionado] = useState<string>();
  const perfis = consulta.data ?? [];
  const atual = perfis.find((p) => p.id === selecionadoId) ?? perfis[0];

  return (
    <div className="grid gap-6">
      <CabecalhoPagina
        titulo="Perfis e permissões"
        descricao="Um perfil é um conjunto de permissões. Os de plataforma valem na instalação; os de empresa, dentro de cada empresa."
      />
      {consulta.isPending && <Skeleton className="h-96 rounded-painel" />}
      {consulta.isError && (
        <EstadoVazio
          tom="perigo"
          icone={<ShieldCheck />}
          titulo="Perfis indisponíveis"
          texto={mensagemDeErro(consulta.error)}
        />
      )}
      {atual && (
        <div className="grid gap-5 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <nav aria-label="Perfis" className="grid content-start gap-1">
            {perfis.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-current={p.id === atual.id || undefined}
                onClick={() => {
                  setSelecionado(p.id);
                }}
                className={cx(
                  'grid gap-0.5 rounded-cartao px-3 py-2 text-left transition-colors',
                  p.id === atual.id ? 'bg-marca-suave text-marca' : 'hover:bg-superficie-hover',
                )}
              >
                <span className="font-medium">{p.nome}</span>
                <span className="text-xs text-texto-fraco">
                  {p.escopo === 'PLATAFORMA' ? 'Plataforma' : 'Empresa'}, {p.permissoes.length}{' '}
                  permissões, {p.emUso} em uso
                </span>
              </button>
            ))}
          </nav>
          <Editor key={atual.id} perfil={atual} />
        </div>
      )}
    </div>
  );
}
